import type { FC } from "react";
import React, { useState } from "react";
import cn from "classnames";
import { useIsomorphicLayoutEffect } from "@litres-libs/global-hooks";
import { useSelector } from "react-redux";
import type { RootState } from "@litres-core/store";
import { useTranslation } from "@litres-libs/i18n";
import { SmartLink } from "@litres-modules/smart-link";
import DefaultIcon from "./icons/empty-avatar.svg";
import styles from "./ProfileButton.module.scss";

interface ProfileButtonProps {
  id: number;
  href: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  className?: string;
  imgClassName?: string;
  size: number;
  hasAvatar: boolean;
  isGradient?: boolean;
  hash?: string;
  imageURL?: string;
  isPDA?: boolean;
}

export const BASE_URL = "https://cdn.litres.ru";
export const USER_AVATAR_SIZES = [30, 40, 50, 500];

export const ProfileButton: FC<ProfileButtonProps> = props => {
  const {
    hasAvatar,
    id,
    size,
    className,
    isGradient = false,
    imgClassName,
    imageURL,
    onClick,
    hash = "",
    href,
    isPDA = false,
  } = props;
  const { t } = useTranslation();
  const profilePrivacy = useSelector(
    (state: RootState) => state.foundationUser.settings.profile_privacy
  );
  const shouldShowAvatar = hasAvatar && profilePrivacy !== "closed";

  const [imagePath, setImagePath] = useState<string>(DefaultIcon);

  useIsomorphicLayoutEffect(() => {
    if (imageURL) {
      setImagePath(
        BASE_URL +
          imageURL.replace(String(id), String(id) + `_${getAvatarSize()}`)
      );
    } else {
      if (shouldShowAvatar) {
        setImagePath(getAvatarUrlWithHash());
      } else {
        setImagePath(DefaultIcon);
      }
    }
  }, [imageURL, hasAvatar]);

  const getAvatarSize = (): number => {
    return USER_AVATAR_SIZES.includes(size)
      ? size
      : USER_AVATAR_SIZES.reduce((prev, curr) => {
          return Math.abs(curr - size) < Math.abs(prev - size) ? curr : prev;
        });
  };

  const getAvatarUrl = (): string => {
    const avatarSize = getAvatarSize();

    return getUserImageURL(id, avatarSize);
  };

  const getAvatarUrlWithHash = (): string =>
    hash ? `${getAvatarUrl()}?v=${hash}` : getAvatarUrl();

  const getUserImageURL = (userID: number, avatarSize: number): string => {
    const path = `${"000000000000".substr(
      0,
      12 - userID.toString().length
    )}${userID}`;
    return `${BASE_URL}/pub/avatar/100/${path.substr(0, 2)}/${path.substr(
      2,
      2
    )}/${path.substr(4, 2)}/${path.substr(6, 2)}/${path.substr(
      8,
      2
    )}/${path.substr(10, 2)}/${userID}_${avatarSize}.jpg`;
  };

  const getAvatarImg = (): JSX.Element => {
    return (
      <div
        className={cn(styles.profileButton__logo_container, {
          [styles.profileButton__logo_container_gradient]: isGradient,
        })}
      >
        <img
          className={cn(imgClassName, styles.profileButton__logo)}
          src={imagePath}
          onError={(): void => {
            setImagePath(DefaultIcon);
          }}
          alt="profile photo"
        />
      </div>
    );
  };

  return (
    <SmartLink
      href={href}
      className={cn(styles.profileButton, className, {
        [styles.profileButton_mobile]: isPDA,
      })}
      onClick={onClick}
      data-testid="header__profile-button"
    >
      {getAvatarImg()}
      <div className={cn(styles.profileButton__title, className)}>
        {t("Профиль")}
      </div>
    </SmartLink>
  );
};
